import { useScrollReveal } from "@/hooks/useScrollReveal";

export interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface ProcessSectionProps {
  title?: string;
  subtitle?: string;
  steps?: ProcessStep[];
}

const defaultSteps: ProcessStep[] = [
  {
    number: "01",
    icon: "ri-search-line",
    title: "Browse Our Inventory",
    description:
      "Explore our curated selection of 60+ quality pre-owned vehicles online or visit our Farmington lot in person. Filter by body type, price, or brand.",
  },
  {
    number: "02",
    icon: "ri-file-check-line",
    title: "Get Pre-Qualified",
    description:
      "Complete our quick online pre-qualification form and know your buying power before stepping on the lot. No hard credit pull required.",
  },
  {
    number: "03",
    icon: "ri-steering-2-line",
    title: "Schedule Your Test Drive",
    description:
      "Book a no-pressure test drive with one of our expert consultants. We help you find the right fit — not just any fit.",
  },
  {
    number: "04",
    icon: "ri-key-2-line",
    title: "Drive Away Happy",
    description:
      "Finalize with transparent paperwork, flexible financing, and your included 15-day warranty. Drive home the same day.",
  },
];

const BG_IMAGE =
  "https://public.readdy.ai/ai/img_res/ef8383005cd914ea9eb174a6f63f6b47.jpg";

function StepCard({
  step,
  index,
  isVisible,
}: {
  step: ProcessStep;
  index: number;
  isVisible: boolean;
}) {
  return (
    <div
      className={`relative flex flex-col transition-all duration-600 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${index * 130}ms` }}
    >
      {/* Step number — large watermark */}
      <div
        className="absolute -top-3 -left-2 font-serif text-white/5 font-bold leading-none select-none pointer-events-none"
        style={{ fontSize: "6rem" }}
      >
        {step.number}
      </div>

      {/* Card */}
      <div className="relative bg-white/6 border border-white/12 rounded-2xl p-7 flex flex-col h-full hover:border-gold-DEFAULT/40 hover:bg-white/9 transition-all duration-300">
        {/* Icon */}
        <div className="w-14 h-14 rounded-xl bg-gold-DEFAULT/15 border border-gold-DEFAULT/25 flex items-center justify-center mb-5 flex-shrink-0">
          <i className={`${step.icon} text-gold-DEFAULT text-2xl`} />
        </div>

        {/* Step badge */}
        <span className="inline-block text-gold-DEFAULT/80 text-xs font-bold font-sans tracking-[0.2em] uppercase mb-2">
          Step {step.number}
        </span>

        <h3 className="font-serif text-white text-xl md:text-2xl mb-3 leading-tight">
          {step.title}
        </h3>
        <p className="text-white font-sans text-sm leading-relaxed">
          {step.description}
        </p>

        {/* Bottom gold accent line */}
        <div className="mt-6 h-px bg-gradient-to-r from-gold-DEFAULT/40 to-transparent" />
      </div>
    </div>
  );
}

export default function ProcessSection({
  title = "Your Path to the Perfect Vehicle",
  subtitle = "We have streamlined the car buying process so you spend less time on paperwork and more time behind the wheel.",
  steps = defaultSteps,
}: ProcessSectionProps) {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollReveal();

  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      aria-labelledby="process-heading"
    >
      {/* Background image — visible but text-safe */}
      <div className="absolute inset-0">
        <img
          src={BG_IMAGE}
          alt=""
          className="w-full h-full object-cover object-center"
          loading="lazy"
        />
        {/* Show the image but darken enough for legibility */}
        <div className="absolute inset-0 bg-charcoal/78" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-transparent to-charcoal/50" />
      </div>

      {/* Gold accent line top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-DEFAULT/50 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-5 justify-center">
            <span className="h-px w-10 bg-gold-DEFAULT" />
            <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
              HOW IT WORKS
            </span>
            <span className="h-px w-10 bg-gold-DEFAULT" />
          </div>
          <h2
            id="process-heading"
            className="font-serif text-white leading-tight max-w-2xl mx-auto mb-5"
            style={{ fontSize: "clamp(2rem, 3.5vw, 3.2rem)" }}
          >
            {title}
          </h2>
          <p className="text-white font-sans text-base max-w-lg mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Step Cards */}
        <div
          ref={stepsRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {steps.map((step, i) => (
            <StepCard
              key={step.number}
              step={step}
              index={i}
              isVisible={stepsVisible}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://www.uagmn.com/get-pre-qualified/"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 bg-gold-DEFAULT text-charcoal font-bold text-sm px-10 py-4 rounded-full hover:bg-gold-light transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-wide"
          >
            Start Your Journey Today
            <div className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1 duration-300">
              <i className="ri-arrow-right-line text-base" />
            </div>
          </a>
          <a
            href="tel:9527693208"
            className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold text-sm px-10 py-4 rounded-full hover:border-white/60 hover:bg-white/8 transition-all duration-200 whitespace-nowrap cursor-pointer"
            aria-label="Call (952) 769-3208"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-gold-DEFAULT" />
            </div>
            (952) 769-3208
          </a>
        </div>
      </div>

      {/* Gold accent line bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-DEFAULT/40 to-transparent" />
    </section>
  );
}
